import { getBlogCategories, getBlogCategoryCounts } from './directus'

/**
 * Category Utilities
 *
 * Handles conversion between category names and URL slugs for SEO-friendly category pages.
 * Example: "Lawn Care Tips" → "lawn-care-tips"
 */

// All valid categories (used as fallback when Directus doesn't return a category)
// These match the categories defined in Directus
export const ALL_CATEGORIES = [
  "Grass Types",
  "Lawn Care Tips",
  "Watering",
  "Fertilizing",
  "Mowing & Maintenance",
  "Pest Control",
  "Seasonal Care",
  "Climate & Regional Tips",
  "Tech & Tools",
  "Problem Solving",
  "Weed Control",
  "Seeding/Overseeding & Establishment",
  "Lawn Care",
  "Partnerships"
] as const

// Categories shown inline on the blog filter bar. The rest collapse into a "More" dropdown.
// Order intentionally puts evergreen high-traffic topics first.
export const PRIMARY_BLOG_CATEGORIES = [
  "Lawn Care Tips",
  "Mowing & Maintenance",
  "Watering",
  "Fertilizing",
  "Pest Control",
  "Weed Control",
  "Seasonal Care",
] as const

// Per-category accent color used for the card's top border and the filter-pill dot.
// Shared chroma/lightness across hues so the palette reads as one family.
const CATEGORY_COLOR_MAP: Record<string, string> = {
  "Climate & Regional Tips": "oklch(0.62 0.13 220)",
  "Fertilizing": "oklch(0.62 0.13 95)",
  "Grass Types": "oklch(0.62 0.13 145)",
  "Lawn Care Tips": "oklch(0.62 0.13 165)",
  "Mowing & Maintenance": "oklch(0.62 0.13 195)",
  "Pest Control": "oklch(0.62 0.13 25)",
  "Problem Solving": "oklch(0.62 0.13 305)",
  "Seasonal Care": "oklch(0.62 0.13 60)",
  "Seeding/Overseeding & Establishment": "oklch(0.62 0.13 130)",
  "Tech & Tools": "oklch(0.62 0.13 265)",
  "Watering": "oklch(0.62 0.13 240)",
  "Weed Control": "oklch(0.62 0.13 340)",
}

const DEFAULT_CATEGORY_COLOR = "oklch(0.62 0.13 145)"

export function getCategoryColor(category: string | undefined | null): string {
  if (!category) return DEFAULT_CATEGORY_COLOR
  return CATEGORY_COLOR_MAP[category] || DEFAULT_CATEGORY_COLOR
}

/**
 * Convert a category name to a URL-friendly slug
 * @param category - The category name (e.g., "Lawn Care Tips")
 * @returns URL slug (e.g., "lawn-care-tips")
 */
export function categoryToSlug(category: string): string {
  return category
    .toLowerCase()
    .replace(/&/g, 'and')           // Replace & with 'and'
    .replace(/[^a-z0-9]+/g, '-')    // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '')        // Remove leading/trailing hyphens
}

// Pre-computed slug-to-name mapping for fast lookups (computed after categoryToSlug is defined)
const SLUG_TO_CATEGORY_MAP: Record<string, string> = Object.fromEntries(
  ALL_CATEGORIES.map(cat => [categoryToSlug(cat), cat])
)

/**
 * Convert a URL slug back to a category name by matching against known categories
 * First checks the provided categories list, then falls back to ALL_CATEGORIES
 * @param slug - The URL slug (e.g., "lawn-care-tips")
 * @param categories - List of known category names from Directus
 * @returns The matching category name or null if not found
 */
export function slugToCategory(slug: string, categories: string[]): string | null {
  // First, try to match against categories from Directus (categories with posts)
  const directusMatch = categories.find(cat => categoryToSlug(cat) === slug)
  if (directusMatch) return directusMatch

  // Fall back to ALL_CATEGORIES for valid slugs that may not have posts yet
  return SLUG_TO_CATEGORY_MAP[slug] || null
}

/**
 * Check if a slug is a valid category slug (even if it has no posts)
 * @param slug - The URL slug to validate
 * @returns boolean indicating if the slug is valid
 */
export function isValidCategorySlug(slug: string): boolean {
  return slug in SLUG_TO_CATEGORY_MAP
}

/**
 * Category with metadata for rendering
 */
export interface CategoryWithMeta {
  name: string
  slug: string
  count: number
}

/**
 * Get all categories with their slugs and post counts
 * @returns Array of categories with metadata
 */
export async function getCategoriesWithMeta(): Promise<CategoryWithMeta[]> {
  try {
    const [categories, counts] = await Promise.all([
      getBlogCategories(),
      getBlogCategoryCounts()
    ])

    return categories.map(name => ({
      name,
      slug: categoryToSlug(name),
      count: counts[name] || 0
    })).filter(cat => cat.count > 0) // Only include categories with posts
  } catch (error) {
    console.error('Error fetching categories with meta:', error)
    return []
  }
}

/**
 * Get a single category by slug
 * @param slug - The URL slug to look up
 * @returns Category metadata or null if not found
 */
export async function getCategoryBySlug(slug: string): Promise<CategoryWithMeta | null> {
  const categories = await getCategoriesWithMeta()
  return categories.find(cat => cat.slug === slug) || null
}

/**
 * Generate SEO-friendly description for a category
 * @param categoryName - The category name
 * @param postCount - Number of posts in category
 * @returns SEO description string
 */
export function getCategoryDescription(categoryName: string, postCount: number): string {
  const descriptions: Record<string, string> = {
    'Grass Types': postCount > 0
      ? `Explore ${postCount} articles about different grass varieties, identification guides, and which grass types work best for your climate and lawn conditions.`
      : `Discover articles about different grass varieties, identification guides, and which grass types work best for your climate and lawn conditions.`,
    'Lawn Care Tips': postCount > 0
      ? `Discover ${postCount} expert lawn care tips covering everything from basic maintenance to advanced techniques for a healthier, greener lawn.`
      : `Expert lawn care tips covering everything from basic maintenance to advanced techniques for a healthier, greener lawn.`,
    'Watering': postCount > 0
      ? `Learn proper watering techniques with ${postCount} articles on irrigation schedules, water conservation, and keeping your lawn hydrated.`
      : `Learn proper watering techniques, irrigation schedules, water conservation, and keeping your lawn hydrated.`,
    'Fertilizing': postCount > 0
      ? `Master lawn fertilization with ${postCount} guides on NPK ratios, application timing, and choosing the right fertilizer for your grass type.`
      : `Master lawn fertilization with guides on NPK ratios, application timing, and choosing the right fertilizer for your grass type.`,
    'Mowing & Maintenance': postCount > 0
      ? `Get ${postCount} mowing tips and maintenance guides for optimal cutting height, frequency, and equipment care.`
      : `Mowing tips and maintenance guides for optimal cutting height, frequency, and equipment care.`,
    'Pest Control': postCount > 0
      ? `Protect your lawn with ${postCount} articles on identifying and treating common lawn pests, diseases, and weed problems.`
      : `Protect your lawn by identifying and treating common lawn pests, diseases, and weed problems.`,
    'Seasonal Care': postCount > 0
      ? `Plan your lawn care year-round with ${postCount} seasonal guides for spring, summer, fall, and winter maintenance.`
      : `Plan your lawn care year-round with seasonal guides for spring, summer, fall, and winter maintenance.`,
    'Climate & Regional Tips': postCount > 0
      ? `Find region-specific advice in ${postCount} articles tailored to your climate zone and local growing conditions.`
      : `Region-specific lawn care advice tailored to your climate zone and local growing conditions.`,
    'Tech & Tools': postCount > 0
      ? `Explore ${postCount} reviews and guides on lawn care equipment, smart irrigation, and the latest lawn care technology.`
      : `Reviews and guides on lawn care equipment, smart irrigation, and the latest lawn care technology.`,
    'Problem Solving': postCount > 0
      ? `Troubleshoot lawn issues with ${postCount} diagnostic guides and solutions for common lawn problems.`
      : `Troubleshoot lawn issues with diagnostic guides and solutions for common lawn problems.`,
  }

  if (postCount > 0) {
    return descriptions[categoryName] ||
      `Browse ${postCount} articles about ${categoryName.toLowerCase()} to improve your lawn care knowledge and achieve a healthier lawn.`
  }

  return descriptions[categoryName] ||
    `Articles about ${categoryName.toLowerCase()} to improve your lawn care knowledge and achieve a healthier lawn.`
}

/**
 * Related categories mapping for cross-linking sparse pages
 * Maps each category to topically related categories (in priority order)
 */
const RELATED_CATEGORIES: Record<string, string[]> = {
  'Grass Types': ['Climate & Regional Tips', 'Seasonal Care', 'Lawn Care Tips'],
  'Lawn Care Tips': ['Seasonal Care', 'Mowing & Maintenance', 'Fertilizing'],
  'Watering': ['Seasonal Care', 'Climate & Regional Tips', 'Lawn Care Tips'],
  'Fertilizing': ['Seasonal Care', 'Lawn Care Tips', 'Problem Solving'],
  'Mowing & Maintenance': ['Tech & Tools', 'Lawn Care Tips', 'Seasonal Care'],
  'Pest Control': ['Problem Solving', 'Seasonal Care', 'Lawn Care Tips'],
  'Seasonal Care': ['Climate & Regional Tips', 'Lawn Care Tips', 'Fertilizing'],
  'Climate & Regional Tips': ['Grass Types', 'Seasonal Care', 'Watering'],
  'Tech & Tools': ['Mowing & Maintenance', 'Lawn Care Tips', 'Watering'],
  'Problem Solving': ['Pest Control', 'Lawn Care Tips', 'Fertilizing'],
}

/**
 * Get related categories for a given category
 * @param categoryName - The category to find related categories for
 * @returns Array of related category names in priority order
 */
export function getRelatedCategories(categoryName: string): string[] {
  return RELATED_CATEGORIES[categoryName] || ['Lawn Care Tips', 'Seasonal Care']
}

/**
 * Get category keywords for SEO
 * @param categoryName - The category name
 * @returns Array of relevant keywords
 */
export function getCategoryKeywords(categoryName: string): string[] {
  const baseKeywords = ['lawn care', 'grass', 'lawn maintenance', 'yard care']

  const categoryKeywords: Record<string, string[]> = {
    'Grass Types': ['grass identification', 'grass varieties', 'cool season grass', 'warm season grass', 'grass types guide'],
    'Lawn Care Tips': ['lawn tips', 'lawn advice', 'lawn care guide', 'healthy lawn', 'lawn improvement'],
    'Watering': ['lawn watering', 'irrigation', 'watering schedule', 'sprinkler', 'water conservation'],
    'Fertilizing': ['lawn fertilizer', 'NPK', 'fertilizer schedule', 'lawn nutrients', 'fertilizer application'],
    'Mowing & Maintenance': ['mowing tips', 'cutting height', 'lawn mower', 'grass cutting', 'lawn maintenance'],
    'Pest Control': ['lawn pests', 'weed control', 'lawn disease', 'grub control', 'lawn insects'],
    'Seasonal Care': ['spring lawn care', 'summer lawn', 'fall lawn prep', 'winter lawn', 'seasonal lawn tips'],
    'Climate & Regional Tips': ['regional lawn care', 'climate zones', 'local grass types', 'zone specific care'],
    'Tech & Tools': ['lawn equipment', 'lawn tools', 'smart irrigation', 'lawn care technology', 'mower reviews'],
    'Problem Solving': ['lawn problems', 'brown spots', 'dead grass', 'lawn repair', 'lawn troubleshooting'],
  }

  return [...baseKeywords, ...(categoryKeywords[categoryName] || [categoryName.toLowerCase()])]
}
